import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { format } from 'date-fns';

import { axiosPrivate } from 'app/utils';

import { organisationFeatureFlagsQueryKey } from '../../../hooks/useOrganisationFeatureFlags';
import type { BillingCycle } from '../../../hooks/useSubscription';

export interface UpgradeWebsitePlanParams {
  organisationId: string;
  billing: BillingCycle;
  price: { amount: number; currency: string };
  goLiveDate?: Date;
}

function upgradeWebsitePlan({
  organisationId,
  billing,
  price,
  goLiveDate,
}: UpgradeWebsitePlanParams) {
  return axiosPrivate.post(`/v3/pharmacist/organisations/${organisationId}/upgrade-website-plan`, {
    billingPeriod: billing,
    price,
    // The API whitelists the body, so an absent go-live date is omitted rather than sent
    // as null. Formatting locally keeps the calendar's date instead of shifting it in UTC.
    ...(goLiveDate ? { goLiveDate: format(goLiveDate, 'yyyy-MM-dd') } : {}),
  });
}

export type UpgradeWebsitePlanResult = UseMutationResult<
  AxiosResponse<void>,
  Error,
  UpgradeWebsitePlanParams
>;

export function useUpgradeWebsitePlan(): UpgradeWebsitePlanResult {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upgradeWebsitePlan,
    // The upgrade grants web builder access server-side; refetching keeps the wizard from
    // offering the upsell again.
    onSuccess: (_data, { organisationId }) =>
      queryClient.invalidateQueries({ queryKey: organisationFeatureFlagsQueryKey(organisationId) }),
    meta: { errorMessage: 'Failed to upgrade your website plan' },
  });
}
